import React from "react";
import { useAuth } from "../contexts/AuthContext";
import { Navigate, useLocation } from "react-router-dom";

const AdminProtectedRoute = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-400 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-white">Verifying Admin Access...</h2>
          <p className="text-orange-200">Please wait...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user is admin or super admin
  const hasAdminAccess = user?.is_admin || user?.email === 'admin@underwater-macro-awards.com';
  const hasSuperAdminAccess = hasAdminAccess || user?.email?.includes('superadmin');

  // For super admin routes, require super admin access
  if (location.pathname.includes('/super-admin') && !hasSuperAdminAccess) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-red-400 mb-4">Access Denied</h2>
            <p className="text-red-200 mb-6">
              You need Super Admin privileges to access this interface.
            </p>
            <p className="text-orange-200 text-sm mb-4">
              Current user: {user?.email}
            </p>
            <button 
              onClick={() => window.location.href = '/'}
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded"
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // For regular admin routes, require admin access
  if (!hasAdminAccess) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-red-400 mb-4">Admin Access Required</h2>
            <p className="text-red-200 mb-6">
              You need administrator privileges to access this interface.
            </p>
            <p className="text-orange-200 text-sm mb-4">
              Current user: {user?.email} | Admin: {user?.is_admin ? 'Yes' : 'No'}
            </p>
            <button 
              onClick={() => window.location.href = '/'}
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded"
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // User has proper access, render the admin interface
  return children;
};

export default AdminProtectedRoute;